// ============================================================================
// AttachmentChip — small file chip for chat attachments
// ============================================================================
//
// Used both in the ChatPanel input (with a remove button) and on user
// MessageBubbles (read-only) to show an attached file's name + type icon.

import { Box, Typography, IconButton } from '@mui/material'
import {
  Close,
  PictureAsPdf,
  Image as ImageIcon,
  TableChart,
  Description,
  InsertDriveFile,
} from '@mui/icons-material'
import { beoflowColors, spacing, borderRadius, typography } from '@/theme/muiTheme'

const XLSX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

function renderIcon(mediaType: string, color: string) {
  const sx = { fontSize: 16, color, flexShrink: 0 }
  if (mediaType === 'application/pdf') return <PictureAsPdf sx={sx} />
  if (mediaType.startsWith('image/')) return <ImageIcon sx={sx} />
  if (mediaType === XLSX_MEDIA_TYPE) return <TableChart sx={sx} />
  if (mediaType === 'text/csv' || mediaType === 'text/plain') return <Description sx={sx} />
  return <InsertDriveFile sx={sx} />
}

interface AttachmentChipProps {
  filename: string
  mediaType: string
  onRemove?: () => void
  /** Render on a colored (user) bubble — uses light-on-dark colors. */
  onBubble?: boolean
}

export default function AttachmentChip({
  filename,
  mediaType,
  onRemove,
  onBubble = false,
}: AttachmentChipProps) {
  const fg = onBubble ? '#FFF' : 'text.secondary'

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: spacing.xs,
        maxWidth: '220px',
        px: spacing.sm,
        py: spacing.xs,
        borderRadius: borderRadius.medium,
        backgroundColor: onBubble
          ? 'rgba(255, 255, 255, 0.18)'
          : 'rgba(248, 246, 244, 0.08)',
        border: onBubble
          ? '1px solid rgba(255, 255, 255, 0.25)'
          : `1px solid ${beoflowColors.graphGreyDark}`,
      }}
    >
      {renderIcon(mediaType, fg)}
      <Typography
        sx={{
          fontSize: typography.fontSizeTiny,
          color: fg,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={filename}
      >
        {filename}
      </Typography>
      {onRemove && (
        <IconButton
          size="small"
          onClick={onRemove}
          aria-label={`Remove ${filename}`}
          sx={{ p: 0, ml: spacing.xs, color: fg, flexShrink: 0 }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  )
}
